import { useMemo, useState, useEffect } from 'react';
import { emissionsFromParsed, applyElectricityFactor } from '../lib/emissions';
import api from '../lib/api';
import useEmissionFactors from '../hooks/useEmissionFactors';
import { Leaf, Car, Trees, Fuel, Info, ChevronDown } from 'lucide-react';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from './ui/tooltip';

const fmt = (n, d = 0) => {
  if (n == null || Number.isNaN(n)) return '0';
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: d, minimumFractionDigits: d });
};

// Region-aware source citations shown in per-factor tooltips.
const SOURCE_TEXT = {
  US: {
    Electricity: 'US EPA eGRID — total-output CO₂e rate for your local eGRID subregion, matched from the bill\u2019s ZIP code (falls back to state, then US national average).',
    'Natural Gas': 'US EPA GHG Emission Factors Hub — CO₂ per therm. CCF ×1.037, Mcf ×10.37, Dth ×10.',
    Water: 'Embedded-energy estimate for water supply & treatment — not an official factor; varies by utility. CGAL ×100, CCF ×748 gal.',
    Sewer: 'Embedded-energy estimate for wastewater collection & treatment — not an official factor. CGAL ×100 gal.',
  },
  AU: {
    Electricity: 'Australia DCCEEW National Greenhouse Accounts Factors — Scope 2 electricity emission factor (state applied when known).',
    'Natural Gas': 'Australia DCCEEW National Greenhouse Accounts Factors — natural gas. CCF ×1.037, Mcf ×10.37, Dth ×10.',
    Water: 'Embedded-energy estimate for water supply & treatment — not an official factor; varies by utility. CGAL ×100, CCF ×748 gal.',
    Sewer: 'Embedded-energy estimate for wastewater collection & treatment — not an official factor. CGAL ×100 gal.',
  },
  UK: {
    Electricity: 'UK DESNZ/Defra GHG Conversion Factors 2025 — UK grid electricity (location-based Scope 2).',
    'Natural Gas': 'UK DESNZ/Defra 2025 — natural gas 0.18293 kg CO₂e/kWh (converted to per therm). CCF ×1.037, Mcf ×10.37, Dth ×10.',
    Water: 'Embedded-energy estimate for water supply & treatment — not an official factor; varies by utility. CGAL ×100, CCF ×748 gal.',
    Sewer: 'Embedded-energy estimate for wastewater collection & treatment — not an official factor. CGAL ×100 gal.',
  },
  EU: {
    Electricity: 'EEA / Ember 2024 — per-country electricity carbon intensity, resolved from the bill\u2019s country (approximate; refine per AIB residual mix).',
    'Natural Gas': 'IPCC / DEFRA natural gas ≈0.183 kg CO₂e/kWh (converted to per therm). CCF ×1.037, Mcf ×10.37, Dth ×10.',
    Water: 'Embedded-energy estimate for water supply & treatment — not an official factor; varies by utility. CGAL ×100, CCF ×748 gal.',
    Sewer: 'Embedded-energy estimate for wastewater collection & treatment — not an official factor. CGAL ×100 gal.',
  },
  CA: {
    Electricity: 'Canada ECCC v3.0 (NIR 1990–2023) — provincial electricity consumption intensity (province applied when known).',
    'Natural Gas': 'Canada ECCC v3.0 — marketable natural gas ~1921 g CO₂/m³ (converted to per therm). CCF ×1.037, Mcf ×10.37, Dth ×10.',
    Water: 'Embedded-energy estimate for water supply & treatment — not an official factor; varies by utility. CGAL ×100, CCF ×748 gal.',
    Sewer: 'Embedded-energy estimate for wastewater collection & treatment — not an official factor. CGAL ×100 gal.',
  },
};

const REGION_LABEL_FALLBACK = {
  US: 'US EPA eGRID national-average & GHG Emission Factors',
  AU: 'Australia — DCCEEW National Greenhouse Accounts Factors',
  UK: 'United Kingdom — DESNZ/Defra Conversion Factors',
  EU: 'Europe — EEA / Ember per-country grid factors',
  CA: 'Canada — ECCC Emission Factors (NIR)',
};

// Plain-language explanations shown when hovering each equivalency card.
const EQUIV_SOURCES = {
  miles: 'Picture driving an average petrol car this far — that trip would release about the same CO₂ as this bill. (EPA: 0.393 kg per mile.)',
  seedlings: 'This many young trees would each need to grow for 10 years to soak up this much CO₂. (EPA: ~60 kg absorbed per seedling.)',
  gasoline: 'The same amount of CO₂ you would get from burning this many gallons of petrol/gasoline. (EPA: 8.887 kg per gallon.)',
};

const InfoTip = ({ text }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button type="button" className="text-emerald-500/70 hover:text-emerald-700 focus:outline-none" aria-label="Source & factor">
        <Info className="h-3 w-3" />
      </button>
    </TooltipTrigger>
    <TooltipContent className="max-w-xs"><p className="text-xs leading-snug">{text}</p></TooltipContent>
  </Tooltip>
);

// Carbon-footprint card for a parsed utility bill (EPA factors).
// `parsed` = { commodity, usage_details, meter_reads, accounts }
export const EmissionsCard = ({ parsed, compact = false, defaultOpen = false }) => {
  const { version: efVersion } = useEmissionFactors();
  const emissions = useMemo(() => emissionsFromParsed(parsed || {}), [parsed, efVersion]);
  const [expanded, setExpanded] = useState(defaultOpen);
  const [refined, setRefined] = useState(null);

  // US electricity: refine via ZIP → eGRID subregion (server-side crosswalk).
  useEffect(() => {
    setRefined(null);
    const ri = emissions.resolveInput;
    if (!emissions.hasData || emissions.region !== 'US' || !ri || !ri.postcode) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/emission-factors/resolve', {
          params: { country: ri.country || '', state: ri.state || '', zip: ri.postcode || '', year: ri.year || '' },
        });
        const nf = data?.factors?.electricity_kg_per_kwh;
        if (!cancelled && nf > 0 && data.resolved_by === 'zip') {
          setRefined(applyElectricityFactor(emissions, nf, {
            subregion: data.subregion, subregion_code: data.subregion_code,
            label: data.label, vintage_year: data.vintage_year, resolved_by: data.resolved_by,
          }));
        }
      } catch { /* keep the client-side estimate */ }
    })();
    return () => { cancelled = true; };
  }, [emissions]);

  if (!emissions.hasData) {
    return (
      <div className="border border-emerald-200 bg-emerald-50/40 p-4" data-testid="emissions-card-empty">
        <div className="flex items-center gap-2 text-emerald-800">
          <Leaf className="h-4 w-4" />
          <span className="text-sm font-semibold">Carbon footprint</span>
        </div>
        <p className="text-xs text-emerald-700/80 mt-1">
          No usage quantity found on this bill yet — add meter reads or usage to estimate emissions.
        </p>
      </div>
    );
  }

  const view = refined || emissions;
  const totalKg = view.total_kg;
  const showMt = totalKg >= 1000;
  const eq = view.equivalencies;
  const region = SOURCE_TEXT[view.region] ? view.region : 'US';
  const srcMap = SOURCE_TEXT[region] || SOURCE_TEXT.US;
  const regionLabel = view.region_label || REGION_LABEL_FALLBACK[region] || REGION_LABEL_FALLBACK.US;
  const zipNote = view.resolved_by === 'zip'
    ? ` · eGRID ${view.subregion_code} · from ZIP ${view.resolveInput?.postcode}`
    : (view.subregion_code ? ` · ${view.subregion_code}` : '');

  const calcContent = (
    <div className="space-y-1" data-testid="emissions-calc-breakdown">
      <p className="font-semibold text-xs">How this is calculated</p>
      {view.items.map((it, i) => (
        <div key={i} className="text-[11px] leading-snug">
          {it.type}: {fmt(it.normQty, it.normUom === 'therms' ? 1 : 0)} {it.normUom} × {it.factor} kg = {fmt(it.kg)} kg
          {it.type === 'Electricity' ? zipNote : ''}
        </div>
      ))}
      <div className="text-[11px] font-semibold border-t border-white/20 pt-1">
        Total = {showMt ? `${fmt(view.total_mt, 2)} t` : `${fmt(totalKg)} kg`} CO₂e
      </div>
      <p className="text-[10px] opacity-80 leading-snug">
        Standard CO₂e = CO₂ + CH₄ + N₂O (GWP-weighted). Emission factors from {regionLabel}{view.vintage_year ? ` (${view.vintage_year})` : ''}{view.resolved_by === 'zip' ? ' — electricity matched by ZIP' : (view.subregion_code ? ` — ${view.subregion_code}` : '')}. Water uses an embedded-energy estimate.
      </p>
    </div>
  );

  return (
    <TooltipProvider delayDuration={150}>
      <div className="border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50/50 p-4 space-y-3" data-testid="emissions-card">
        {/* Header (always visible) — click to expand the details */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => setExpanded((v) => !v)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded((v) => !v); } }}
          className="w-full flex items-center gap-2 cursor-pointer select-none"
          data-testid="emissions-toggle"
          aria-expanded={expanded}
        >
          <span className="h-9 w-9 flex items-center justify-center bg-emerald-600 text-white rounded-full shrink-0">
            <Leaf className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold flex items-center gap-1">
              Estimated carbon footprint
              <span onClick={(e) => e.stopPropagation()}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-emerald-500/70 hover:text-emerald-700 focus:outline-none" aria-label="How this is calculated" data-testid="emissions-calc-info">
                      <Info className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">{calcContent}</TooltipContent>
                </Tooltip>
              </span>
            </div>
            <div className="text-2xl font-black tracking-tight text-emerald-900" data-testid="emissions-total">
              {showMt ? `${fmt(view.total_mt, 2)} t` : `${fmt(totalKg, 0)} kg`} CO₂e
            </div>
            {view.region_label ? (
              <div className="text-[10px] text-emerald-700/70 mt-0.5" data-testid="emissions-region-label">
                {view.region_label}{view.subregion_code ? ` · ${view.subregion_code}` : ''}{view.resolved_by === 'zip' ? ' (by ZIP)' : ''}
              </div>
            ) : null}
          </div>
          <ChevronDown className={`h-5 w-5 text-emerald-600 shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
        </div>

        {expanded && (
          <div className="space-y-3" data-testid="emissions-details">
            {/* Per-commodity breakdown */}
            <div className="space-y-1">
              {view.items.map((it, i) => {
                const src = srcMap[it.type];
                return (
                  <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-emerald-100 last:border-0" data-testid={`emissions-line-${i}`}>
                    <span className="text-emerald-900 font-medium">{it.type}</span>
                    <span className="text-emerald-700/80 font-mono flex items-center gap-1">
                      {fmt(it.normQty, it.normUom === 'therms' ? 1 : 0)} {it.normUom} · {it.factorLabel}
                      {src ? <InfoTip text={`${it.factorLabel} — ${src}`} /> : null}
                    </span>
                    <span className="font-mono font-semibold text-emerald-900 w-24 text-right">
                      {it.kg >= 1000 ? `${fmt(it.kg / 1000, 2)} t` : `${fmt(it.kg, 0)} kg`}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Equivalencies — hover any card for a plain-language meaning */}
            {!compact && (
              <div className="grid grid-cols-3 gap-2 pt-1">
                <Equiv icon={Car} value={`${fmt(eq.miles)} mi`} label="driven by car" tip={EQUIV_SOURCES.miles} />
                <Equiv icon={Trees} value={fmt(eq.seedlings)} label="tree seedlings · 10 yr" tip={EQUIV_SOURCES.seedlings} />
                <Equiv icon={Fuel} value={`${fmt(eq.gasoline_gal)} gal`} label="gasoline burned" tip={EQUIV_SOURCES.gasoline} />
              </div>
            )}

            <div className="flex items-start gap-1.5 text-[10px] text-emerald-700/70 pt-1">
              <Info className="h-3 w-3 mt-0.5 shrink-0" />
              <span>
                Estimate using {regionLabel}.
                {view.hasEstimate ? ' Water uses an embedded-energy estimate.' : ''} Hover any factor or card for a plain-language explanation. Actuals vary by region.
              </span>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

const Equiv = ({ icon: Icon, value, label, tip }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <div className="bg-white/70 border border-emerald-100 p-2 text-center cursor-help">
        <Icon className="h-4 w-4 mx-auto text-emerald-600 mb-1" />
        <div className="text-sm font-bold text-emerald-900 leading-tight">{value}</div>
        <div className="text-[9px] uppercase tracking-wide text-emerald-700/70">{label}</div>
      </div>
    </TooltipTrigger>
    <TooltipContent className="max-w-xs"><p className="text-xs leading-snug">{tip}</p></TooltipContent>
  </Tooltip>
);

export default EmissionsCard;
